

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { TableFilter } from "../types/TableFilter";
import { removePref, setPref, setJSONPref } from "@/lib/storage/userPrefs";
import {
  filterChanged,
  isFilterChanged,
} from "@/lib/utils/changes-spotter";
import { updateFilter as updateFilterApi } from "@/lib/api/handlers/ViewFilterHandling";
import { throwServerError } from "@/lib/api/errors/ErrorHandler";
import {
  tableCacheKey,
  type AdhocSurface,
} from "@/lib/storage/adhocTableState";
import { hasNonEmptyConditions } from "@/components/ConditionBuilder/condition-utils";

// Define the context
type FiltersContextType = {
  filtersRef: React.RefObject<TableFilter[]>;
  hasMoreRef: React.RefObject<boolean>;
  activeFilterState: TableFilter;
  setActiveFilterState: (newFilter: TableFilter) => void;
  activateFilter: (newFilter: TableFilter) => void;
  deleteFilterCookie: () => void;
  updateFilter: (newFilter: TableFilter) => Promise<void>;
  /**
   * The provider's `persist` prop, exposed for consumers that must know
   * whether they sit on the canonical list surface (`true`) or on a
   * popup-scoped mount such as the send-email recipient picker (`false`):
   * the JSON filter transfer renders its buttons only on the former.
   */
  persist: boolean;
};

const FiltersContext = createContext<FiltersContextType | undefined>(undefined);

/** Table types whose list/data queries live under a per-scope namespace. */
export type TableNamespaceType =
  | "event"
  | "guest"
  | "registrant"
  | "companion"
  | "emailSent"
  | "eventLimits"
  | "checkIn"
  | "actionLog"
  | "contact"
  | "speaker"
  | "sponsor";

/**
 * Bust every cached query under the table namespace a filter/view belongs to
 * (table data + init bundle + filter/view lists). Single source of truth for
 * filter AND view CRUD — historically only filter EDIT invalidated, so created
 * filters vanished and deleted ones resurrected on a sub-staleTime remount.
 * `scopeId` = eventId (companyId for "contact"); unused for "event" (root).
 */
export function invalidateTableNamespace(
  queryClient: ReturnType<typeof useQueryClient>,
  type: TableNamespaceType,
  scopeId?: string,
): void {
  const namespaces: string[] =
    type === "event" ? ["root"]
    : type === "guest" ? ["guestlist"]
    : type === "registrant" || type === "companion" ? ["guestsRequests"]
    : type === "emailSent" ? ["sentMails", "msgMail"]
    : type === "checkIn" ? ["checkInHistory"]
    : type === "actionLog" ? ["eventAutomationHistory"]
    : type === "contact" ? ["crmContacts"]
    : type === "speaker" ? ["speakersHub"]
    : type === "sponsor" ? ["sponsorsHub"]
    : [];
  for (const ns of namespaces) {
    const baseKey = ns === "root" ? [ns] : [ns, scopeId];
    void queryClient.invalidateQueries({ queryKey: baseKey });
  }
}

// Provider component
export function FiltersProvider({
  activeFilter,
  filters,
  eventId,
  children,
  type,
  persist = true,
}: {
  activeFilter?: TableFilter;
  filters?: TableFilter[];
  eventId?: string;
  children: React.ReactNode;
  /**
   * When false, this provider performs NO userPrefs IO — no writes on
   * activate, no DEFAULT-cleanup removal on mount, no cookie delete. Use on
   * non-canonical surfaces that mount a provider under the SAME pref keys as
   * a page-level table (send-email dialog on mail-templates, standalone
   * guest-detail popup — both `guest`-typed with the guest list's keys), so
   * a filter chosen there never leaks into — or silently erases — the
   * canonical surface's persisted filter.
   */
  persist?: boolean;
  type:
    | "event"
    | "guest"
    | "registrant"
    | "companion"
    | "emailSent"
    | "eventLimits"
    | "checkIn"
    | "actionLog"
    // CRM contact pool — `eventId` carries the company id on this surface.
    // v1 has no named contact filters, so `updateFilter` never fires here.
    | "contact"
    // Wave A — speaker/sponsor hub DataTables (event-scoped).
    | "speaker"
    | "sponsor";
}) {
  const queryClient = useQueryClient();
  const filtersRef = useRef<TableFilter[]>(
    type !== "eventLimits" ? [activeFilter!, ...(filters ?? [])] : [],
  );
  const [activeFilterState, setActiveFilterState] = useState<TableFilter>(
    type !== "eventLimits" ? activeFilter! : ({} as TableFilter),
  );
  const hasMoreRef = useRef<boolean>(
    type !== "eventLimits" && (filters?.length ?? 0) >= 11,
  );

  // Determine cookie name based on type and eventId
  const getCookieName = useCallback(() => {
    if (type === "event") {
      return `filter-event`;
    } else {
      return `filter-${type}-${eventId}`;
    }
  }, [type, eventId]);

  useEffect(() => {
    if (type === "eventLimits" || !persist) return;
    if (activeFilterState?.name === "DEFAULT") {
      removePref(getCookieName());
    }
  }, [type, persist, getCookieName, activeFilterState?.name]);

  const activateFilter = useCallback(
    (newFilter: TableFilter) => {
      if (type === "eventLimits") return;

      if (filterChanged(activeFilterState, newFilter)) {
        setActiveFilterState(newFilter);
        const selectedIndex = filtersRef.current.findIndex(
          (filter) => filter.name === newFilter.name,
        );

        if (selectedIndex > 0) {
          const [selectedFilter] = filtersRef.current.splice(selectedIndex, 1);
          filtersRef.current.unshift(selectedFilter);
        }

        if (persist) {
          if (newFilter.name === "DEFAULT") {
            removePref(getCookieName());
            // Ad-hoc (DEFAULT) filters have no id to persist server-side, so
            // remember the applied conditions locally — silently re-applied on
            // return instead of resetting to no filter. (Named filters are
            // already sticky via their stored id + re-fetch.)
            //
            // An EMPTY ad-hoc filter is not a filter, and storing one was not
            // harmless: the editor dual-writes `conditions` on every change,
            // so clearing the last row persisted a present-but-empty group.
            // On the next visit that block outranked the flat rows a dashboard
            // click-through injects — the row filtered the list and counted in
            // the badge while the editor rendered empty. Drop the snapshot
            // instead, which also restores the plain "no filter" default.
            // Through the shared builder: the ROOT event list's cache key
            // carries the company id (the id pref cannot leak across companies,
            // a DEFAULT-anchored cache entry can — see `tableCacheKey`).
            const adhocKey = tableCacheKey(
              "filter",
              type as AdhocSurface,
              String(eventId),
            );
            if (
              !newFilter.filter?.length &&
              !hasNonEmptyConditions(newFilter.conditions)
            ) {
              removePref(adhocKey);
            } else {
              setJSONPref(adhocKey, newFilter);
            }
          }

          if (newFilter.tableFilterId !== "DEFAULT") {
            setPref(getCookieName(), newFilter.tableFilterId!);
            // A named filter wins; drop any stale ad-hoc snapshot.
            removePref(
              tableCacheKey("filter", type as AdhocSurface, String(eventId)),
            );
          }
        }
      }
    },
    [type, persist, activeFilterState, getCookieName, eventId],
  );

  const deleteFilterCookie = useCallback(() => {
    if (type === "eventLimits" || !persist) return;
    removePref(getCookieName());
  }, [type, persist, getCookieName]);

  const updateFilter = useCallback(
    async (newFilter: TableFilter) => {
      if (type === "eventLimits") return;

      // Empty-guard must account for conditions-only filters: a nested OR
      // tree is not flat-representable, so its derived `filter` is [] — the
      // flat-length check alone silently swallowed every EDIT of such a
      // filter (local state updated, PUT never sent, edit lost on reload).
      if (
        (newFilter.filter?.length === 0 &&
          !hasNonEmptyConditions(newFilter.conditions)) ||
        newFilter.name.trim()?.length === 0
      )
        return;

      if (!isFilterChanged(newFilter, filtersRef.current, true)) return;

      // Pass the appropriate type to the updateFilterApi
      const response = await updateFilterApi(newFilter, type, eventId);
      if (
        response &&
        typeof response !== "string" &&
        (response as { isServerError?: boolean }).isServerError === true
      )
        throwServerError(response);

      newFilter.updatedAt = new Date();

      const indexRef = filtersRef.current.findIndex(
        (filter) => filter.tableFilterId === newFilter.tableFilterId,
      );

      if (indexRef === 0) {
        filtersRef.current = [newFilter, ...filtersRef.current.slice(1)];
        setActiveFilterState(newFilter);
      } else
        filtersRef.current = [
          filtersRef.current[0],
          newFilter,
          ...filtersRef.current.slice(1, indexRef),
          ...filtersRef.current.slice(indexRef + 1),
        ];

      // Bust any cached query whose key prefix matches the table this filter
      // belongs to. Filter rows feed into the table data + the filter list +
      // the filter detail caches; just nuke the namespace and let consumers
      // refetch.
      invalidateTableNamespace(queryClient, type, eventId);
    },
    [type, eventId, queryClient],
  );

  const contextValue = useMemo(() => ({
    hasMoreRef, filtersRef, activeFilterState, setActiveFilterState,
    activateFilter, deleteFilterCookie, updateFilter, persist,
  }), [activeFilterState, activateFilter, deleteFilterCookie, updateFilter, persist]);

  return (
    <FiltersContext.Provider value={contextValue}>
      {children}
    </FiltersContext.Provider>
  );
}

// Hook for consuming the context
export function useFilters() {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error("useFilter must be used within a FilterProvider");
  }
  return context;
}
